export default {
  overview: {
    title: "Project Overview",
    archive_title: "Archived projects",
    subtitle: "List of all projects.",
    actions: {
      create_new: "create new project",
    },
    search: {
      placeholder: "eg. Name, etc.",
    },
    table: {
      // The unfolded row (right-click or arrow). `counts` are singular | plural, with the figure in bold
      // before them; each leads to the project's register of that name.
      expanded: {
        address: "Address",
        site: "Site",
        no_address: "No address on file",
        leading_users: "Leads",
        no_leading_users: "No lead assigned",
        phases: "Phases",
        no_phases: "No phases yet",
        phase_current: "running",
        created: "Created on {date}",
        open: "Open project",
        counts_failed: "The counts could not be loaded.",
        retry: "Try again",
        contents: "In the project",
        counts: {
          constructionReports: "[[cr]] | [[cr.pl]]",
          hourlyWageReports: "[[hwr]] | [[hwr.pl]]",
          machines: "machine | machines",
          materials: "material | materials",
          additionalEfforts: "additional effort | additional efforts",
        },
      },
      headers: {
        alternative_id: "Identifier",
        name: "Name",
        place: "Place",
        montage_start_date: "Start of Project",
        montage_end_date: "Planned end of Project",
        montage_period: "Project period",
        leading_users: "Leads",
        budget: "Budget",
        createdAt: "Created at",
      },
      empty: {
        place: "Not set",
        montage_period: "No period",
        budget: "No budget",
      },
    },
  },
  // Service catalogue: the bill of quantities agreed with the client in the offer (titles and
  // positions), booked from the construction reports.
  serviceCatalog: {
    loading: "Loading service catalogue…",
    no_matches: "No position matches this filter.",
    add_title: "Title",
    add_title_long: "Add title",
    add_position: "Position",
    add_position_below: "Add position below",
    add_row: "Add a position or title",
    add_subtitle: "Add subtitle",
    missing: "Still missing: {fields}",
    goes_under: "Goes under {title}",
    goes_top: "Goes to the top level",
    discard: "Discard",
    // How the question before leaving the page names a new row still open (an opened one is named by its
    // item number and short text).
    unsaved_new_row: "New row in the service catalogue",
    discard_title: "Discard your input?",
    discard_text: "The open row has not been saved yet. What you typed into it will be lost.",
    statement_print: "Print progress statement…",
    statement_dialog: {
      title: "Print progress statement",
      subtitle: "PDF with quantities and progress per position.",
      mode_total: "Overall",
      mode_period: "Period",
      total_hint: "Everything done up to the cutoff date.",
      period_hint:
        "What the period added, from bookings and progress readings, plus the overall status at its end. Fits a progress claim.",
      cutoff: "Cutoff date",
      from: "From",
      to: "To",
      with_prices: "With prices",
      download: "Download PDF",
      range_invalid: "The start date is after the end date.",
      error: "The progress statement could not be created.",
    },
    completion: "Completion",
    positions: "Positions",
    positions_done: "{done} of {total} done",
    quantity_total: "Quantity",
    contract_value: "Contract value",
    source_manual: "manual",
    earned_value: "Earned value",
    open_value: "Remaining value",
    excess: "Overruns",
    excess_count: "{count} overrun | {count} overruns",
    search: "Search position no., short or long text",
    state: "State",
    origin: "Origin",
    phase: "Phase",
    no_phase: "No phase",
    all: "All",
    filter: "Filter",
    filter_reset: "Reset filters",
    main_contract: "Main contract",
    oz: "Pos. no.",
    kind: "Kind",
    short_text: "Short text",
    long_text: "Long text",
    less: "Less",
    quantity: "Quantity",
    unit: "Unit",
    unit_price: "Unit price",
    total: "Total",
    actual: "Actual",
    actual_of_planned: "Actual / planned",
    progress: "Progress",
    remaining: "{amount} left",
    type: "Position type",
    parent: "Parent title",
    top_level: "Top level",
    order: "Order",
    note: "Note",
    toggle: "Expand or collapse",
    move_up: "Move up",
    move_down: "Move down",
    move_to: "Move to…",
    move_title: "Move",
    delete_title: "Delete {oz}?",
    delete_text: "The entry is removed from the service catalogue.",
    delete_title_cascade:
      "The title and every title and position beneath it will be deleted. Booked positions cannot be deleted.",
    save_error: "The entry could not be saved.",
    position_taken: "This position number is already used in the service catalogue.",
    quantity_invalid: "Enter a valid quantity (≥ 0).",
    unit_price_invalid: "Enter a valid unit price (≥ 0).",
    unit_price_too_large: "This unit price is too large.",
    quantity_too_large: "This quantity is too large.",
    import_hint:
      "Rows are matched by position number, in file order. Without a parent position number the title is derived from the number (2.1.12 → 2.1). Booked quantities are left untouched.",
    kinds: {
      TITLE: "Title",
      POSITION: "Position",
    },
    types: {
      NORMAL: "Normal position",
      OPTIONAL: "Optional position",
      ALTERNATIVE: "Alternative position",
    },
    type_hints: {
      NORMAL: "Counts towards the contract value.",
      OPTIONAL: "Only executed on demand. Not part of the contract value, but what is executed still counts as earned.",
      ALTERNATIVE:
        "An alternative to another position. It is neither bookable nor part of the contract value. Switch to normal once chosen.",
    },
    states: {
      OPEN: "Open",
      IN_PROGRESS: "In progress",
      DONE: "Done",
      EXCESS: "Overrun",
    },
    // Only for activity entries from before progress readings (one adopted quantity per position).
    adopted: {
      quantity: "Adopted quantity",
      date: "As of",
    },
    bookings: {
      report: "Report {nr}",
      allocated_cost: "Allocated cost",
    },
    // Progress reading per cutoff date: a position's total quantity up to and including a day.
    stands: {
      record: "Record progress",
      heading: "Progress as of",
      hint: "Total quantity per position up to and including the cutoff date. Bookings up to that day are part of it.",
      date: "Cutoff date",
      planned: "Planned",
      before: "So far",
      stand: "Reading",
      stand_on: "Reading on {date}",
      opening: "Opening reading",
      from_stand: "Reading {date}",
      booked_since: "+ {amount} booked",
      booked_only: "booked",
      below: "below {amount} so far",
      removed: "will be removed",
      needs_delete: "You are not allowed to remove a progress reading.",
      invalid: "Not a valid quantity",
      too_large: "This quantity is too large",
      date_invalid: "Please enter a cutoff date.",
      problem_hidden: "Invalid input outside the search: {positions}",
      save: "Save ({count})",
      saved: "{count} progress reading as of {date} saved | {count} progress readings as of {date} saved",
      save_error: "The progress readings could not be saved.",
      load_error: "The positions could not be loaded.",
      empty: "No position a progress reading can be recorded for.",
      discard_title: "Discard your input?",
      discard_text: "The progress readings as of {date} are not saved yet.",
      unsaved: "Progress as of {date}",
      excess: "Excess",
    },
    history: {
      title: "History",
      actual: "Actual {actual} of {planned}",
      stand: "Progress reading",
      opening: "Opening reading (no date)",
      delta: "{amount} compared with before",
      covered: "part of the reading of {date}",
      add: "Add progress reading",
      empty: "No progress readings or bookings yet.",
      delete_title: "Delete the progress reading of {date}?",
      delete_opening_title: "Delete the opening reading?",
      delete_text: "The position's actual quantity then follows from the reading before it and the bookings.",
      save_error: "The progress reading could not be saved.",
      load_error: "The history could not be loaded.",
    },
    // Labels for activity entries from the time of the montage parts list.
    legacy_montage: {
      profile: "Profile",
      unit_weight: "Unit weight",
      mounted_quantity: "Mounted",
      status: "Status",
      status_date: "Status date",
      status_labels: {
        PLANNED: "Planned",
        DELIVERED: "Delivered",
        MOUNTED: "Mounted",
        ACCEPTED: "Accepted",
      },
    },
    gaeb: {
      button: "GAEB",
      title: "Import GAEB",
      hint: "Take over a bill of quantities from your estimating software (GAEB DA XML: X83, X84 or X86). You see what changes first.",
      file: "GAEB file",
      drop: "Drag a GAEB file here or browse",
      new: "{count} new",
      changed: "{count} changed",
      unchanged: "{count} unchanged",
      only_in_catalog: "{count} only in catalogue",
      warnings: "{count} notices",
      booked: "booked",
      mode: "Import mode",
      into_change_order: "Assign positions to",
      import: "Import",
      error: "The file could not be read.",
      modes: {
        MERGE_BY_OZ: "Add and update",
        REPLACE_IF_UNBOOKED: "Replace (booked positions stay)",
      },
      states: {
        NEW: "New",
        CHANGED: "Changed",
        UNCHANGED: "Unchanged",
        ONLY_IN_CATALOG: "Only in catalogue",
      },
    },
    change_orders: {
      title: "Change orders",
      single: "Change order",
      add: "Change order",
      hint: "Work beyond the offer. Only approved change orders count towards the contract value; assign positions to a change order in the position dialog.",
      edit: "Edit change order",
      create: "Create change order",
      delete_text: "The change order is removed from the service catalogue.",
      empty: "No change orders yet.",
      number: "No.",
      name: "Title",
      state: "Status",
      submitted_at: "Submitted on",
      decided_at: "Decided on",
      amount_claimed: "Amount claimed",
      delete_blocked: "The change order still has positions",
      status: {
        REQUESTED: "Requested",
        APPROVED: "Approved",
        REJECTED: "Rejected",
      },
    },
  },

  create: {
    title: "Create Project",
    subtitle: "Create a new project.",
  },
  detail: {
    title: "Project Details",
    loading: "Loading project",
    // Counts entries whose price the hourly wage invoice will not be able to calculate with.
    price_gap: {
      tooltip: "Entries without a maintained price. Billing is blocked while they are open.",
    },
    // When the project never opened at all. "denied" covers both "not yours" and "gone", because
    // the backend answers the two alike.
    blocked: {
      denied: {
        title: "No access to this project",
        text: "This project is not assigned to you, or it no longer exists. Ask your site management if you are supposed to work on it.",
      },
      error: {
        title: "The project could not be loaded",
        text: "The server could not be reached. Please try again.",
      },
      retry: "Try again",
      overview: "To the project list",
    },
    hints: {
      no_project_id: "Project firstly needs to be created",
      no_customer: "First assign a customer to this project.",
    },
    tabs: {
      general: "General",
      contacts: "Contacts",
      materials: "Materials",
      additional_efforts: "Additional Effort",
      machines: "Machines",
      categories: "Categories",
      users: "Employees",
      files: "File storage",
      userPositions: "Employee-positions",
      hourlyWageReports: "[[HWR.pl]]",
      constructionReports: "[[CR.pl]]",
      serviceCatalog: "Service catalogue",
      timetracking: "Times",
      phases: "Phases",
      configuration: "Configuration",
      activity: "Activity",
    },
    actions: {
      actions: "Actions",
      planning: {
        create: "Create personnel planning",
        open: "Open in personnel planning",
      },
      invoice: "Start [[hwi]]",
      customer: "Open customer",
      update: "Save",
      duplicate: {
        duplicate: "Duplicate",
        modal: {
          title: "Duplicate project",
          name: "Name",
          alternativeId: "Identifier",
          agree: "Duplicate",
          required: "This field is required",
        },
      },
      // Archived (docs/archiving.md): out of every list and closed to new work, but readable, printable and billable.
      archive: {
        // "Remove": a project something was recorded on moves to the archive, any other is deleted.
        text: "The project disappears from every overview and picker. If anything was recorded on it, it moves to the archive: existing reports stay readable, correctable, printable and billable, and no new reports, time or planning are recorded on it. You can restore it at any time. Otherwise it is deleted.",
        hint: "No new reports, time or planning. What exists stays editable and billable.",
      },
    },
    form: {
      customer: {
        add_customer: "Customer",
        customer_locked: "The customer cannot be changed after the project is created.",
        no_data: "Press <kbd>enter</kbd> to create a new Customer with the company name <strong>{value}</strong>",
        create: {
          title: "Create Customer",
          subtitle: "Create a new Customer",
          form: {
            company: "Company",
          },
        },
        contact: {
          active_subheader: "All project contacts",
          inactive_subheader: "All inactive contacts",
          filter: "Filter contacts",
          no_project_contacts: "No Project contacts set",
          add_contact: "Add new contact",
          edit: "Edit",
          activate: "Add",
          deactivate: "Remove",
          position: {
            no_results_matching: "No results matching <strong>{value}</strong>",
            positions: "Contact Positions",
          },
          email: "Email",
          email_unset: "Unset",
          first_name: "First Name",
          last_name: "Last Name",
          telephone_number: "Telephone Number",
          telephone_number_unset: "Unset",
          mobile_telephone_number: "Mobile Telephone Number",
          mobile_telephone_number_unset: "Unset",
          gender: "Gender",
        },
      },
      general: {
        sections: {
          base_data: "Master data",
          customer: "Customer",
          address: "Location",
          note: "Note",
        },
        name: "Name",
        alternative_id: "Identifier",
        alternative_id_locked: "The identifier cannot be changed after the project is created.",
        montage_start_date: "Start of Project",
        montage_end_date: "Planned end of Project",
        lat: "Lat",
        lng: "Lng",
        note: "Notes",
        no_map_pin: "Not marked on the map",
        set_map_pin_hint: "Click the map to place the pin.",
        // The address fields themselves live under `general.address.*` — they are the
        // same on every screen. Only what makes the project location stays here.
        address_search_unavailable_hint: "Enter the address in the fields below and click the map to set the position.",
      },
      pricing: {
        selling_price: "Selling price",
        surcharge: "Surcharge",
      },
      remove_confirm: {
        title: "Confirm removal",
        text: "Remove “{name}” from the project?",
        agree: "Remove",
      },
      // Purchase prices a project keeps per machine / material (copied from the master record when added).
      purchase_price_variants: {
        title: "Purchase prices in this project",
        variants_count: "{count} price | {count} prices",
        // Under a price the master data holds at another amount; followed by that price.
        master_price: "Master data:",
        // Under the prices, for a master data price the project lacks; preceded by its name, followed by its price.
        master_only: "only in the master data:",
        // Under a price, while bookings are frozen at another price than the one in the field; followed by
        // "Re-price". The re-price runs when the project is saved.
        reprice: {
          stay: "{count} booking stays at {prices} | {count} bookings stay at {prices}",
          stay_no_price: "{count} booking stays without a price | {count} bookings stay without a price",
          // After "… stay at 45.00 EUR" when bookings without a price are among them.
          or_no_price: "or without a price",
          switch: "Re-price",
          switching:
            "{count} booking is re-priced to {price} on save | {count} bookings are re-priced to {price} on save",
          keep: "Don't re-price",
        },
      },
      // Strict workspace: the project's purchase prices are the suppliers', with project prices (docs/suppliers.md,
      // "Objektpreise"). The component appends prices and names itself.
      supplier_prices: {
        no_price: "no price in {currency}",
      },
      machines: {
        empty_customer: "First assign a customer to this project.",
        delete: "Delete",
        add_machine: "Add Machine",
        filter: "Filter machines",
        empty: "No machines assigned",
        add_machine_packet_title: "Packet",
        add_machine_packet_text: "{index} of {length}",
        skip_machine_packet_item: "Do not adopt",
        no_data: "No machines found",
        name: "Name",
        purchase_price: "Purchase Price",
        selling_price: "Selling Price",
        selling_price_text: "Machine Selling Price",
        customer_selling_price: "Customer Selling Price",
        project_selling_price: "Project Selling Price",
        not_set: "Not set",
        dialog_title: "Add Machine",
        device_number: "Device number",
        device_number_add: "Type a number, press Enter to add",
        selling_price_customer_takeover: "Selling Price will be taken over to the customer.",
        customer_origin: "Already saved for this customer",
      },
      materials: {
        empty_customer: "First assign a customer to this project.",
        delete: "Delete",
        add_material: "Add Material",
        filter: "Filter materials",
        empty: "No materials assigned",
        packet: "Packet",
        no_data: "No materials found",
        name: "Name",
        purchase_price: "Purchase Price",
        selling_price: "Selling Price",
        selling_price_text: "Material Selling Price",
        customer_selling_price: "Customer Selling Price",
        project_selling_price: "Project Selling Price",
        not_set: "Not set",
        dialog_title: "Add Material",
        selling_price_customer_takeover: "Selling Price will be taken over to the customer.",
        customer_origin: "Already saved for this customer",
      },
      additional_efforts: {
        empty_customer: "First assign a customer to this project.",
        delete: "Delete",
        add_additional_effort: "Add Additional Effort",
        filter: "Filter additional efforts",
        empty: "No additional efforts assigned",
        packet: "Packet",
        no_data: "No additional efforts found",
        name: "Name",
        purchase_price: "Price",
        selling_price: "Selling Price",
        selling_price_text: "Additional Effort Selling Price",
        customer_selling_price: "Customer Selling Price",
        project_selling_price: "Project Selling Price",
        not_set: "Not set",
        dialog_title: "Add Additional Effort",
        selling_price_customer_takeover: "Selling Price will be taken over to the customer.",
        customer_origin: "Already saved for this customer",
      },
      users: {
        add_user: "Add User",
        selected_count: "{count} selected",
        filter: "Filter users",
        empty: "No users assigned",
        no_data: "No Users found",
        delete_user: "Delete",
        leading_user: "User with leading ability",
        leading_tag: "Lead",
        name: "Name",
        email: "Email",
        created_at: "Created at",
        position: {
          text: "Employee Positions",
          positions: "Positions",
          no_results_matching: "No results matching <strong>{value}</strong>.",
        },
        no_email_set: "-No email-",
      },
      userpositions: {
        no_data: "No Employee-position found",
        add_userposition: "Add Employee-position",
        selected_count: "{count} selected",
        filter: "Filter employee positions",
        empty: "No employee positions assigned",
        delete: "Delete",
        hourlyRate: "Hourly Rate",
        hourlyRateText: "Employee-position Hourly Rate",
        no_customer: "Firstly add a customer",
        remove_confirm_held:
          "Remove “{name}” from the project? One staff member holds this position and loses it. | Remove “{name}” from the project? {count} staff members hold this position and lose it.",
      },
      // Print state as text, shared by construction reports and hourly wage reports. The table
      // gets away with a bare tick because the column header above it says what the tick means —
      // the card list has no header, so the flag there names its own state.
      print_state: {
        printed: "Printed",
        unprinted: "Not printed",
      },
      // Day markers of the report calendars on the hourly wage report and construction report tabs.
      reportCalendar: {
        clear: "Clear",
        // Unfolds the calendar on tablets and phones, where it stands above the list.
        toggle: "Calendar",
        legend: {
          printed: "Printed",
          unprinted: "Not printed",
          missing: "Missing report",
        },
      },
      hourlyWageReports: {
        create: "[[Hwr]]",
        delete: "Delete",
        empty: "No [[hwr.pl]] yet",
        delete_modal: {
          title: "Delete [[hwr]]?",
          text: "[[Hwr]] {nr} will be permanently deleted. This action cannot be undone.",
          agree: "Delete",
          disagree: "Cancel",
        },
        move_title: "Move [[hwr.pl]]",
        print: "Print",
        search: {
          search: "Search",
          startDateFrom: "Execution Date from",
          startDateTo: "Execution Date to",
        },
        actions: {
          print: "Print",
          open: "Open",
          delete: "Delete",
        },
        headers: {
          projectNr: "No",
          categories: "Categories",
          startDate: "Execution Date",
          createdBy: "Created By",
          printed: "Printed",
        },
        no_project_id: "Project firstly needs to be created",
        expand: {
          description: "Description",
          no_description: "No description",
          createdAt: "Created at",
          history: "History",
          index: "Index",
          costing: "Post-calculation",
          attached: "Contained in the [[cr]]",
          separate: "Counted separately",
        },
      },
      reportTransfer: {
        duplicate: "Duplicate",
        move: "Move",
        target: "Project",
      },
      constructionReport: {
        no_project_id: "Project firstly needs to be created",
        search: "Search",
        dateFrom: "Date from",
        dateTo: "Date to",
        create: "[[Cr]]",
        empty: "No [[cr.pl]] yet",
        duplicate_title: "Duplicate [[cr.pl]]",
        duplicate_subtitle: "Each copy needs a day without [[cr:a/an]] [[cr]].",
        duplicate_date: "Copy on",
        weather_kept: "Weather taken from {date}",
        move_title: "Move [[cr.pl]]",
        move_taken: "{project} already has [[cr:a/an]] [[cr]] on {date}.",
        delete_modal: {
          title: "Delete [[cr]]?",
          text: "[[Cr]] {nr} will be permanently deleted. This action cannot be undone.",
          agree: "Delete",
          disagree: "Cancel",
        },
        expand: {
          service_entries: "Booked services",
          services: "Free services",
          history: "History",
          index: "Index",
          no_details: "No details recorded",
        },
        actions: {
          print: "Print",
          open: "Open",
          delete: "Delete",
        },
        headers: {
          projectNr: "No",
          date: "Date",
          services: "Daily Services",
          createdBy: "Created By",
        },
      },
      configuration: {
        default_currency: "Currency",
        currency_change_note: "Can only be changed when project has no [[cr.pl]] and no [[hwr.pl]]",
        // With suppliers, rentals lock the currency too.
        currency_change_note_rentals: "Can only be changed when project has no [[cr.pl]], no [[hwr.pl]] and no rentals",
        work_days: "Default work days",
        hide_hourly_wage_report_user_names: "Hide Employee Names in [[hwr.pl]]",
        hide_hourly_wage_report_user_names_change_note:
          "Changing this value leads to creating a new index for all printed [[hwr.pl]]",
        attach_hourly_wage_reports_to_construction_reports: "Attach [[hwr.pl]] to the [[cr]]",
        attach_hourly_wage_reports_to_construction_reports_hint:
          "Their costs are then contained in the [[cr]] and are not counted separately in the post-calculation. As long as the project has no [[cr]] yet, they are counted separately. Applies to every [[hwr]] without its own choice.",
        pdf_templates: "PDF templates",
        pdf_templates_hint:
          "Which template this project prints its documents with. Without a choice the default template applies. A change applies to future prints.",
        pdf_template_default: "Default: {name}",
        pdf_template_missing: "Deleted template",
        options: "Options",
      },
      phases: {
        title: "Project Phases",
        period: "Project period",
        days: "days",
        no_dates: "No dates",
        name: "Phase Name",
        start_date: "Start Date",
        start_short: "Start",
        end_date: "End Date",
        end_short: "End",
        save: "Save",
        cancel: "Cancel",
        add: "Add Phase",
        empty_list: 'No phases added yet. Click "Add Phase" to get started.',
        average_capacity: "Average Capacity",
        average_capacity_label: "Average Capacity:",
        average_capacity_help:
          "Employees needed per working day of the phase. Applies to every day that has no custom capacity.",
        custom_capacities: "Custom Capacities",
        from_date: "From Date",
        to_date: "To Date",
        capacity: "Capacity",
        add_capacity: "Add",
        override_warning: "This will override existing capacities:",
        validation: {
          required: "{field} is required",
          min_date: "{field} cannot be before {date}",
          max_date: "{field} cannot be after {date}",
          date_order: "Start date must be before end date",
          name_required: "Phase name is required",
        },
      },
    },
  },
  costs: {
    title: "Running Post-Calculation",
    subtitle: "Project costs over time and drift from budget.",
    action: "Running Post-Calculation",
    header: {
      projects: "Projects",
      range: "Time range",
      range_from: "From",
      range_to: "To",
      view_mode: "View",
      mode_heatmap: "Heatmap",
      mode_chart: "Trend",
      mode_week: "Weeks",
      mode_analysis: "Analysis",
      mode_services: "Services",
      valuation_cost: "Cost",
      valuation_selling: "Revenue",
      no_projects_selected: "Select one or more projects",
      projects_count: "{selected} of {total} projects selected",
      // Until the server has said how many there are: a count, no claimed total.
      projects_count_selected: "{selected} selected",
      select_all: "Select all",
      clear_all: "Clear selection",
    },
    presets: {
      days_30: "30 d",
      days_90: "90 d",
      ytd: "YTD",
      month: "Month",
    },
    budget: {
      title: "Budget",
      title_help:
        "A spending cap for this project: the most you plan to spend in total. The app compares the share used with how complete the project is. Spending a lot early and then pausing for months stays on track as long as progress keeps pace.",
      amount: "Budget amount",
      amount_help: "The maximum you plan to spend on this project (cost, not revenue). Leave empty for no cap.",
      amount_positive: "Enter an amount greater than 0, or leave empty to clear the budget.",
      amount_too_large: "This budget amount is too large.",
      status_help:
        "Budget use compared to completion: watch when spend runs more than {threshold} percentage points ahead, critical from {critical} points, over above 100 %. Without a completion it is a plain cap: watch from {cap} %, critical from 100 % used.",
      completion: "Completion (%)",
      completion_help:
        "How much of the project is done. A project with a service catalogue uses its completion; otherwise enter the value here by hand and keep it current.",
      completion_placeholder: "Not recorded",
      completion_invalid: "Enter a whole number from 0 to 100, or leave empty.",
      completion_from_catalog: "From the service catalogue. Changes with every booking on [[cr:a/an]] [[cr]].",
      completion_updated: "Last updated {date}",
      completion_stale: "Last updated {date}. Please check",
      completion_line: "Completion: {percent} % ({source})",
      completion_not_set: "No completion recorded, cap only",
      health_tooltip: "{used} % of budget used at {completion} % completion ({source})",
      health_tooltip_no_completion: "{used} % of budget used, no completion recorded",
      source: {
        SERVICE_CATALOG: "service catalogue",
        MANUAL: "manual",
        NONE: "not recorded",
        MIXED: "mixed",
      },
      not_set: "No budget set",
      set_budget: "Set budget",
      edit: "Edit budget",
      reset: "Reset",
    },
    plan: {
      title: "Running calculation",
      title_help:
        "Is this project still profitable? Enter the price you agreed with the customer and your target profit. The app then shows your live margin as costs get booked, so there is no need to wait until the job is finished.",
      contract_value: "Contract value",
      contract_from_catalog:
        "From the service catalogue (main contract plus approved change orders). The value entered by hand is kept as a fallback.",
      source_catalog: "service catalogue",
      contract_value_help:
        "The net price agreed with the customer (without VAT), the money you expect to be paid for this job. Your profit is already part of this price; the profit surcharge is not added on top.",
      contract_positive: "Enter an amount greater than 0, or leave empty to clear it.",
      contract_too_large: "This contract value is too large.",
      profit_surcharge: "Profit surcharge",
      profit_surcharge_help:
        "The profit you aim for, as a percentage on top of your costs (e.g. 10%). It is already contained in the contract value and is taken back out of it to work out the target cost the job must stay under.",
      margin_basis_help:
        "Margin = projected profit ÷ contract value, i.e. a share of revenue. The profit surcharge is calculated on cost instead, so the two percentages are never the same: a 10% surcharge equals a 9.1% margin.",
      profit_short: "profit",
      profit_positive: "Enter 0 or more.",
      profit_too_large: "This profit surcharge is too large.",
      target_cost: "Target cost",
      target_cost_help:
        "The most this job may cost and still reach your target profit (contract value ÷ (1 + profit %)). Stay below this and you're on plan.",
      cost_to_date: "Cost to date",
      cost_to_date_help:
        "Everything booked on this project so far (hours, machines and materials) across its whole history, not just the selected time range.",
      projected_profit: "Projected profit",
      projected_profit_help:
        "Contract value minus the cost so far: your profit if nothing more were spent. Green means profit, red means the job is running at a loss.",
      margin_trend_help:
        "Change in the projected margin over the last 30 days (in percentage points). A downward arrow signals a cost drift: the margin is falling.",
      preview: "Result preview",
      preview_help: "How the figures will look once you save, recalculated exactly by the server.",
      not_set: "No contract value set",
      set_plan: "Set plan",
      edit: "Edit plan",
      reset: "Reset",
    },
    employer_contribution: {
      incl: "incl. employer's contribution {amount}",
      column_help: "Labour cost includes the employer's contribution and is therefore higher than hours × rate.",
      range_note:
        "Labour figures are full costs and include {amount} of employer's contribution in the selected range.",
      help: "Labour cost is grossed up by the employer's share of social security (default rate in Settings, overridable per employee). Revenue is not grossed up, so the surcharge lowers the margin.",
    },
    analysis: {
      cost_structure: "Cost structure",
      revenue_structure: "Revenue structure",
      project_ranking: "Project profitability",
      project_ranking_hint: "Projects by projected margin, from most to least profitable.",
      report_ranking: "Most expensive reports",
      report_ranking_revenue: "Highest-revenue reports",
      report: "Report",
      date: "Date",
      cost: "Cost",
      revenue: "Revenue",
      kind_hwr: "HW",
      kind_cr: "CR",
      no_data: "No data in the selected range.",
      no_overlays: "Enable a report overlay ([[hwr.pl]] / [[cr.pl]]) to see the figures.",
    },
    manual: {
      title: "Manual costs & revenues",
      subtitle: "Extra items not derived from reports. Costs count toward spend, revenues raise the projected profit.",
      add: "Add",
      add_title: "Add entry",
      edit_title: "Edit entry",
      empty: "No manual entries yet.",
      date: "Date",
      type: "Type",
      category: "Category",
      description: "Description",
      amount: "Amount",
      direction: {
        COST: "Cost",
        REVENUE: "Revenue",
      },
      cancel: "Cancel",
      save: "Save",
      save_error: "Could not save the entry.",
      amount_positive: "Enter an amount greater than 0.",
      amount_too_large: "This amount is too large.",
    },
    // Rentals (docs/suppliers.md): part of the machine costs. Edited on the project (Machines).
    rentals: {
      title: "Rentals",
      subtitle: "Included in the machine costs.",
      open: "To the machines",
      device: "Device",
      period: "Period",
      cost: "Cost",
      rent: "Rent",
      kinds: {
        RENT: "Rent",
        // A rental's accessory, on its day: "Accessory · Bucket 60 cm · Mietpark Nord"
        ACCESSORY: "Accessory",
        DELIVERY: "Delivery",
        PICKUP: "Pick-up",
      },
    },
    // A report booking of a rented device on a rental day: the rental carries the cost.
    in_rental: "included in rental",
    services: {
      title: "Service catalogue",
      subtitle: "Completion, contract value and earned value against the cost so far.",
      completion: "Completion",
      positions: "{done} of {total} positions done",
      excess: "{count} with overrun",
      contract_value: "Contract value",
      source_manual: "manual",
      earned_value: "Earned value",
      spent_to_date: "Cost so far",
      earned_vs_spent: "Earned minus cost",
      earned_vs_spent_help:
        "Positive means the site has earned more than it cost. Negative means it is working ahead of its money.",
      requested_change_orders: "Requested change orders",
      earned_without_contract: "Earned without an order",
      open_tab: "Open service catalogue",
      analysis_title: "Post-calculation per service",
      analysis_subtitle:
        "Per position: agreed, performed, spent. Each [[cr]]'s cost is spread over the positions booked on it.",
      cutoff: "Cutoff date",
      sort_tree: "Outline",
      sort_margin: "Losses first",
      empty: "No service catalogue.",
      col_contract: "Contract",
      col_earned: "Earned value",
      col_target_cost: "Target cost",
      col_cost: "Actual cost",
      col_margin: "Contribution margin",
      col_margin_percent: "Margin %",
      total: "Total",
      allocated: "Allocated cost",
      unallocated: "Not allocated",
      unallocated_help:
        "Cost not assigned to any position: [[hwr.pl]], manual costs and [[cr.pl]] without booked services.",
      stand_chip: "Reading",
      stand_help: "{amount} from the progress reading of {date}, without cost from [[cr.pl]]",
      stand_help_opening: "{amount} from the opening reading, without cost from [[cr.pl]]",
      uncosted: "Earned value without report cost",
      uncosted_help:
        "Earned value only a progress reading stands behind. No cost is allocated to it, so the margin of these positions reads too high.",
    },
    defaults: {
      load_recent: "Load recently visited projects",
      select_all: "Select all projects",
      empty_title: "Pick projects",
      empty_subtitle: "Pick one or more projects, or load your recently visited ones.",
    },
    status: {
      on_track: "On track",
      warning: "Watch",
      critical: "Critical",
      over: "Over budget",
      unknown: "No budget",
    },
    streams: {
      hourly: "Labor",
      machine: "Machines",
      material: "Materials",
      effort: "Other",
      total: "Total",
      selling_total: "Revenue",
    },
    week_table: {
      week: "Calendar week",
      week_prefix: "CW",
    },
    combined: {
      all_projects: "All projects",
      selected_projects: "Selected projects ({count})",
    },
    health: {
      revenue: "Revenue",
      margin: "Margin",
      percent_of_budget: "{percent}% of budget",
      percent_of_budget_vs_completion: "{percent}% of budget at {completion}% completion",
      no_budgets: "No budgets set. Set a budget per project to track progress.",
      projects_budgeted: "{budgeted} of {total} budgeted",
      contract_value: "Contract value",
      projected_profit: "Projected profit",
      plan_help:
        "Whole-project figures, independent of the selected time range: total contract value against the total cost booked so far, and the profit projected if nothing more is spent. (The revenue/margin above is only for the selected range.)",
      no_plans: "No plan set. Set a contract value per project to track margin.",
      projects_planned: "{planned} of {total} with a plan",
      mixed_currency: "Projects use different currencies. Combined totals may be inaccurate.",
    },
    legend: {
      actual: "Actual",
      today: "Today",
      less: "Less",
      more: "More",
      intensity: "Per project, log scale",
      contained: "Only reports contained in [[cr:a/an]] [[cr]]",
    },
    expand: "Show trend",
    collapse: "Hide trend",
    open_project: "Open project",
    rate_notice:
      "Machines and materials at the purchase price fixed when the report was saved; hourly rates as currently configured. Values may differ from actual invoicing.",
    no_reports: "No reports in the selected range.",
    loading: "Loading cost data…",
    load_error: "Failed to load cost data.",
    invalid_range: "Invalid time range: the start date is after the end date.",
    chart_aria: "Cumulative cost line chart",
    construction_reports: {
      toggle: "Show [[cr.pl]]",
      title: "[[Cr.pl]]",
      entry: "[[Cr]]",
    },
    hourly_wage_reports: {
      toggle: "Show [[hwr.pl]]",
      title: "[[Hwr.pl]]",
      entry: "[[Hwr]]",
      contained: "in [[cr]] {number}",
      contained_unlinked: "in [[cr]]",
      contained_hint: "Contained in the [[cr]] and already counted there, so it is not added again.",
      contained_cell_hint: "Contained in the [[cr]], not counted twice",
      contained_cell_hint_partial: "Also holds [[hwr.pl]] contained in the [[cr]] and not counted twice",
    },
    construction_report_costs: {
      action: "Cost breakdown",
      title: "[[Cr]] cost breakdown",
      title_selling: "[[Cr]] revenue breakdown",
      back: "Back",
      print: "Print",
      open_report: "Open [[cr]]",
      loading: "Loading costs…",
      load_error: "Failed to load costs.",
      print_error: "Failed to generate PDF.",
      report_number: "Report no.",
      date: "Date",
      customer: "Customer",
      project: "Project",
      shift: "Shift",
      duration: "Duration",
      break: "Break",
      work_time: "Work time",
      labor: "Labor costs",
      labor_selling: "Labor revenue",
      incl_employer_contribution: "incl. employer's contribution {amount}",
      machines: "Machine costs",
      machines_selling: "Machine revenue",
      materials: "Material costs",
      materials_selling: "Material revenue",
      position: "Position",
      machine: "Machine",
      material: "Material",
      count: "Count",
      hours: "Hours",
      days: "Days",
      usage: "Usage",
      billed: "Billed",
      rate: "Hourly rate",
      unit_price: "Unit price",
      amount: "Amount",
      cost: "Cost",
      subtotal: "Subtotal",
      total: "Total cost",
      selling_price: "Selling price",
      selling_rate: "Selling rate",
      revenue: "Revenue",
      selling_total: "Total revenue",
      no_data: "No cost data available.",
      overnight: "Overnight",
      overnight_hint: "This shift runs past midnight; its end time is on the following day.",
      overnight_machine_hint:
        "{count} day-rate device(s) billed an additional day of use because a shift runs past midnight.",
      overnight_machines_notice:
        "Day-rate machines used in a shift running past midnight are billed an additional day of use.",
      rate_notice:
        "This cost breakdown is based on the purchase prices fixed when the report was saved and the current hourly rates. " +
        "Hour-rate machines count their shifts' work time (or the deviating time of use recorded), " +
        "day-rate machines every started day of use.",
      selling_notice:
        "This breakdown shows the selling prices (revenue) in the project's selling unit; a percentage surcharge applies to each booking's fixed purchase price. " +
        "Hour-rate machines count their shifts' work time (or the deviating time of use recorded), " +
        "day-rate machines every started day of use. Positions without a configured selling price are shown as “—”.",
    },
    hourly_wage_report_costs: {
      action: "Cost breakdown",
      title: "[[Hwr]] cost breakdown",
      title_selling: "[[Hwr]] revenue breakdown",
      back: "Back",
      print: "Print",
      open_report: "Open [[hwr]]",
      loading: "Loading costs…",
      load_error: "Failed to load costs.",
      print_error: "Failed to generate PDF.",
      attached_note:
        "This [[hwr]] is attached to the [[cr]]: its costs are contained there and are not counted separately in the post-calculation.",
      not_in_post_calculation: "Not counted in the post-calculation",
      contained_chip: "In [[cr]] {number}",
      contained_chip_unnumbered: "Contained in [[cr:a/an]] [[cr]]",
      open_construction_report: "Open [[cr]]",
      report_number: "Report no.",
      date: "Date",
      customer: "Customer",
      project: "Project",
      labor: "Labor costs",
      labor_selling: "Labor revenue",
      incl_employer_contribution: "incl. employer's contribution {amount}",
      machines: "Machine costs",
      machines_selling: "Machine revenue",
      materials: "Material costs",
      materials_selling: "Material revenue",
      efforts: "Additional efforts",
      position: "Position",
      machine: "Machine",
      material: "Material",
      effort: "Additional effort",
      count: "Count",
      hours: "Hours",
      duration: "Usage",
      billed: "Billed",
      rate: "Hourly rate",
      unit_price: "Unit price",
      amount: "Amount",
      cost: "Cost",
      total: "Total cost",
      selling_price: "Selling price",
      selling_rate: "Selling rate",
      revenue: "Revenue",
      selling_total: "Total revenue",
      no_data: "No cost data available.",
      rate_notice:
        "This cost breakdown is based on the purchase prices fixed when the report was saved and the current hourly rates. " +
        "Day-rate machines are billed per started day of use.",
      selling_notice:
        "This breakdown shows the selling prices (revenue) in the project's selling unit; a percentage surcharge applies to each booking's fixed purchase price. " +
        "Positions without a configured selling price are shown as “—”.",
    },
  },
  component: {
    autocomplete: {
      search_by_name: "Name",
    },
    fetch_project: {
      text: "Fetch Project",
    },
    report_panel: {
      resize: "Resize",
      resize_hint: "Drag to resize · double-click: default width",
      release_to_minimize: "Release to minimize",
      minimize: "Minimize",
      restore: "Restore",
      full: "Full width",
      exit_full: "Exit full width",
      close: "Cancel",
    },
  },
  validation: {
    unique_machine_device_identifier_for_project: "Device Identifier {value} is already existent",
  },
}
